import FileInput from "components/blocks/FileInput";
import FilePreview from "components/blocks/FileInput/FilePreview";
import useMutation from "hooks/useMutation";
import { useNotification } from "hooks/useNotification";
import { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FileService } from "services/fileService";
import { FileUploadResponse } from "services/shared-models/sharedTypes";
import {useCookieContext} from "../../contexts";

export type SelectedFilesType = FileUploadResponse & { file: File; size?: string; showDeleteButton?: boolean , createdOn?: string, createdBy?: string};

const FileUploadWithPreview = ({
                                   maxNumberOfFiles,
                                   withAsterisk,
                                   files,
                                   setFiles,
                                   formProps = {},
                                   onChange,
                                   label,
                                   clickHereForLabel,
                                   error,
                                   setValue,
                                   onDelete,
                                   isDisabled = false,
                                   isMultiple= false,
                                   AddOnly=false,
                                   newFiles = [],
                                   uploadWithUser = false
                               }: {
    maxNumberOfFiles?: number;
    withAsterisk?: boolean;
    isDisabled?: boolean;
    AddOnly?: boolean;
    isMultiple? : boolean;
    formProps?: any;
    files: SelectedFilesType[];
    setFiles: (files: SelectedFilesType[]) => void;
    onChange?: (files: SelectedFilesType[]) => void;
    label?: string;
    clickHereForLabel?: string;
    error?: string;
    setValue?: UseFormSetValue<any & { fileIds: string[] }>;
    onDelete?: (files: SelectedFilesType[]) => void;
    newFiles?: string[];
    uploadWithUser? : boolean;
    allowedFileTypes?:string;
    subTitle?:string;
    maxFileSizeKb?:number

}) => {
    const { t, i18n } = useTranslation(["Orders", "Common"]);
    const { showNotification } = useNotification();
    const {userSession} =  useCookieContext();

    const { mutateAsync: uploadFileAsync, loading: fileUploadLoading } = useMutation({
        queryFn: async (values: File) => {
            return await FileService.uploadFile(values);
        },
    });

    const { mutateAsync: fileDownload, loading: fileDownloadLoading } = useMutation({
        queryFn: async ({id,fileName}: { id:string,fileName:string}) => {
            await FileService.downloadFile(id,fileName);
            return { hasError: false, data: null, description: "", code: 200 };
        },
    });

    const onFileUpload = async (file: File) => {
        if (maxNumberOfFiles && files.length >= maxNumberOfFiles) {
            showNotification({
                description: t("Common:maxNumberOfFilesReached", { maxNumberOfFiles }),
                type: "warning",
            });
            return;
        }

        const response = await uploadFileAsync(file);
        if (response && !response.hasError) {
            const currentUserName = i18n.language === "ar"
                ? userSession?.arabicName
                : userSession?.englishName;
            const newFiles = [
                ...files,
                {
                    fileName: response.data!.fileName,
                    uuid: response.data!.uuid,
                    file,
                    size: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
                    createdOn: new Date().toISOString() ,
                    createdBy: currentUserName ,
                },
            ];

            if (setValue) {
                setValue(
                    "filesIds",
                    newFiles.map((f) => f.uuid),
                );
            }

            setFiles(newFiles);
            onChange && onChange(newFiles);
        }
    };

    const handleDelete = (file: SelectedFilesType) => {
   
        const newFilesList = files.filter(f => f.uuid !== file.uuid);
        // if (setValue) {
        //     setValue("filesIds", newFilesList.map(f => f.uuid));
        // }
        setFiles(newFilesList);
        // onChange && onChange(newFilesList);
         onDelete && onDelete(newFilesList);
    };

    return (
        <div className="d-flex flex-column gap-1 flex-2">
            <FileInput
                labelClassName="fw-bold"
                label={label ? label : t("attachments")}
                withAsterisk={withAsterisk}
                clickHereForLabel={clickHereForLabel ? clickHereForLabel : t("Common:toAttachFile")}
                containerWrapperClassName="flex-grow-1"
                disabled={isDisabled}
                {...formProps}
                onChange={(files) => {
                    
                    if(files instanceof FileList)
                    files && onFileUpload(files[0]);
                    else if (files instanceof  File)
                    files && onFileUpload(files);
                }}
            />
            <div className="d-flex flex-wrap gap-1 align-items-center ">
                {fileUploadLoading && <div className="custom-loader"></div>}
                {files.map((file) => {
                    const isNew = newFiles.includes(file.uuid);
                    return (
                        <div
                            key={file.uuid}
                            className={`file-preview-wrapper position-relative ${isNew ? "file-preview-new" : ""}`}
                        >
                            <FilePreview
                                disabled={isDisabled}
                                loading={fileDownloadLoading}
                                hideDeleteButton={AddOnly || file.showDeleteButton === false }
                                onDelete={AddOnly ? undefined : () => handleDelete(file)}
                                onShow={async () => {
                                    await fileDownload({id:file.uuid,fileName:file.fileName});
                                }}
                                file={file.file}
                                fileSize={file.size}
                                createdOn={file.createdOn}
                                createdBy={file.createdBy}
                            />
                            {isNew && (
                                <span className="file-new-badge" aria-label="New">{t("Common:new", { defaultValue: "New" })}</span>
                            )}
                        </div>
                    );
                })}
            </div>
            {error && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                    {error}
                </div>
            )}
        </div>
    );
};

export default FileUploadWithPreview;
